import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Key, 
  Shield, 
  CreditCard, 
  Users, 
  Settings,
  Calendar,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export default function VehicleOwnersPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#FFD166]/10 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFD166]/10 rounded-full mb-6">
                <Key className="w-5 h-5 text-[#FFD166]" />
                <span className="text-[#FFD166] font-medium text-sm">Dueños de Vehículos</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Monetiza tu Vehículo con
                <span className="text-[#FFD166]"> Mercapoli Move</span>
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                Convierte tu vehículo en una fuente de ingresos.
              </p>
              <p className="text-slate-600 mt-4">
                Renta tu auto de forma segura, establece tus reglas y administra todo desde la plataforma.
              </p>
              <Link to="/register">
                <Button className="btn-primary mt-8 h-14 px-8 text-lg">
                  Registrar mi vehículo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=600&h=400&fit=crop" 
                alt="Vehicle Owner"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Beneficios para Propietarios
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl text-center">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Settings className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Control total</h3>
              <p className="text-slate-600 text-sm">Tú decides cuándo, a quién y a qué precio rentas.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Seguro integrado</h3>
              <p className="text-slate-600 text-sm">Tu vehículo está protegido durante cada renta.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Pagos digitales</h3>
              <p className="text-slate-600 text-sm">Recibe pagos directamente en tu Digi Wallet.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl text-center">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Usuarios verificados</h3>
              <p className="text-slate-600 text-sm">Solo usuarios verificados pueden rentar tu vehículo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            ¿Cómo funciona?
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#00BFA6] text-white flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-1">Registra tu vehículo</h3>
                <p className="text-slate-600">Sube fotos, establece precios y define tu disponibilidad.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FFD166] text-slate-900 flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-1">Recibe solicitudes</h3>
                <p className="text-slate-600">Usuarios verificados te enviarán solicitudes de renta.</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#00887A] text-white flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-1">Genera ingresos</h3>
                <p className="text-slate-600">Entrega tu vehículo y recibe el pago en tu wallet.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#FFD166]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] mb-4">
            Tu vehículo puede generar ingresos
          </h2>
          <p className="text-slate-800 text-lg mb-8">
            Únete a la comunidad de propietarios de Mercapoli Move.
          </p>
          <Link to="/register">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 h-14 px-8 text-lg rounded-full">
              Registrar mi Vehículo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
